import { Checkbox, Dialog, Popover, Tooltip } from "@mui/material";
import {
  ArrowLeft,
  Edit,
  Filter,
  PlusCircle,
  Trash,
  Trash2,
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import API from "../../../API/newIndex";
import BulkEditPosStaff from "./BulkEditPosStaff";
import { DataLoad, PlanPermission } from "../../../App";
import Breadcrumbs from "../../../controllers/breadCrumbs";
import { formatDate } from "../../../controllers/FormatAmount";
import { parentNavigate } from "../../../controllers/ParentNavigate";
import { TableColumnsContext } from "../../../controllers/TableColumnsContext";
import { useUser } from "../../../controllers/UserContext";
import NoRecords from "../../Main/NoRecords";
import Notification from "../../Notification";
import {
  ButtonComponent,
  CommonTable,
  ConfirmationPopup,
  PageLimitDropDownComponent,
  PaginationComponent,
  SearchComponent,
} from "@shopeasy-dashboard-ui/ui-library";
import { isAscendingOrder } from "../../../utilities/staticFunction";
import RenderCustomFieldValue from "../../../utilities/RenderCustomFieldValue";
import { clientData } from "../../../controllers/GetClient";
import * as defaultCustomFields from '../../../utilities/defaultCustomTableField';
import useTableApi from "../../../utilities/useTableApi";

const PosStaff = () => {
  const handleTableApi = useTableApi();
  const location = useLocation();
  const { dataLoading, setDataLoading } = useContext(DataLoad);
  const { setPlanPermission } = useContext(PlanPermission);
  const [staff, setstaff] = useState();
  const [Page, setPage] = useState(1);
  const initialLimit = localStorage.getItem("limit") || "10";
  const [limit, setLimit] = useState(initialLimit);
  const [selectedRowsId, setSelectedRowsId] = useState([]);
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();

  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();

  const savedSort = sortData?.posstaff;

  const [sortBy, setSortBy] = useState(
    () => (location?.state?.accending ? location?.state?.field : savedSort?.sort_by || "createdAt")
  );
  const [orderBy, setOrderBy] = useState(() => {
    if (location?.state?.accending) {
      return { orderByValue: "ASC", isDecending: false };
    }
    const isAsc = isAscendingOrder(savedSort?.order_by);
    return {
      orderByValue: savedSort?.order_by ? (isAsc ? "ASC" : "DESC") : "DESC",
      isDecending: savedSort?.order_by ? !isAsc : true,
    };
  });
  const isInitialSortLoaded = useRef(Boolean(location?.state?.field || savedSort?.sort_by));

  const { tableColumnsData } = useContext(TableColumnsContext);

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.posstaff)) return dataObj.posstaff;
    if (Array.isArray(tableColumnsData?.posstaff)) return tableColumnsData.posstaff;
    return [];
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      "_id",
      "__v",
      "client",
      "tenant",
      "store_client",
      "store_tenant",
      "user",
      "name",
      "first_name",
      "last_name",
      "email",
      "phone",
      "pin",
      "location",
      "pos_location",
      "pos_location_info",
      "createdAt",
      "updatedAt",
      "action",
      "actions",
      "checkbox",
      "selection",
      "select",
      "drag_handle",
      "draghandle",
      "drag",
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === "string" && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes(".");
        const parts = isDotted ? fieldName.split(".") : null;

        const label = fieldName
          .replace(/\./g, " › ")
          .replace(/[-_]/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        return {
          key: fieldName,
          title: label,
          width: "160px",
          minWidth: "110px",
          responsiveWidth: "100px",
          headerClassName: "px-3 whitespace-nowrap truncate",
          cellClassName: "px-3 truncate",
          render: (row) => (
            <RenderCustomFieldValue
              val={getValue(row)}
              fieldName={fieldName}
              clientData={clientData}
            />
          ),
        };
      });
  }, [apiFieldNames]);

  useEffect(() => {
    localStorage.setItem("limit", limit);
  }, [limit]);

  useEffect(() => {
    if (!isInitialSortLoaded.current && isUserLoaded) {
      isInitialSortLoaded.current = true;
      if (savedSort && savedSort.sort_by) {
        const isAsc = isAscendingOrder(savedSort.order_by);
        const targetOrderBy = isAsc ? "ASC" : "DESC";
        if (savedSort.sort_by !== sortBy || targetOrderBy !== orderBy.orderByValue) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: targetOrderBy,
            isDecending: !isAsc,
          });
        }
      }
    }
  }, [savedSort, isUserLoaded, sortBy, orderBy.orderByValue]);

  // Sorting Data
  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "ASC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "ASC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
  };

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };
  const [clearRow, setClearRow] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [search, setSearch] = useState(
    location?.state?.searchkey ? location?.state?.searchkey : "",
  );
  useEffect(() => {
    setSearch(location?.state?.searchkey ? location?.state?.searchkey : "");
  }, [location?.state?.searchkey]);

  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (!isUserLoaded && !isInitialSortLoaded.current && !location?.state?.field) {
      return;
    }

    const trimmed = search?.trim() || "";
    clearTimeout(debounceTimeout.current);
    if (trimmed === "") {
      getStaff();
    } else if (trimmed.length >= 3) {
      debounceTimeout.current = setTimeout(getStaff, 500);
    }
    return () => clearTimeout(debounceTimeout.current);
  }, [search, sortBy, orderBy, isUserLoaded]);

  const getStaff = async () => {
    setDataLoading(true);
    try {
      const orderParam = orderBy?.orderByValue || (orderBy?.isDecending ? "DESC" : "ASC");
      let ApiCall = await API({
        url: `/admin/pos/getPOSstaffs?searchkey=${search?.length > 2 ? search : ""}&sort_by=${sortBy}&order_by=${orderParam}`,
        method: "GET",
      });
      if (ApiCall.status === 200 || ApiCall.status === 304) {
        const result = ApiCall?.data;
        setstaff(result);
      }
    } catch (error) {
      if (error?.response?.status === 882) {
        setPlanPermission({ isPlanPermissions: false, forModule: "pos" });
      }
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    } finally {
      setDataLoading(false);
    }
  };

  // Remove Staff Member
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const HandleDelete = async () => {
    try {
      const targetIds = selectedRowsId?.length > 0 ? selectedRowsId : [deleteId];
      let successCount = 0;
      for (const id of targetIds) {
        if (!id) continue;
        let ApiCall = await API({ url: `/admin/pos/deletePOSstaff/${id}`, method: "post" });
        if (ApiCall.status === 200 || ApiCall.status === 304) {
          successCount++;
        }
      }
      if (successCount > 0) {
        setIsNotification(true);
        setNotificationMsg("Staff Removed Successfully!");
        setSeverity("success");
        setSelectedRowsId([]);
        setDeleteId("");
        setClearRow(!clearRow);
        getStaff();
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    }
    setConfirmationPopup(false);
  };
  const handleCloseConfirm = (value) => {
    setConfirmationPopup(value);
  };

  // Update Staff
  const Handleedit = (id) => {
    parentNavigate(`/pos/staff/${id}`);
  };
  const [filter, setFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const resetFilter = (e) => {
    e.preventDefault();
    setSearch(location?.state?.searchkey ? location?.state?.searchkey : "");
    setPage(1);
    setLimit(limit === "all" ? "10" : limit);
    setFilter(false);
    setAnchorEl(null);
  };

  const handleSelectRow = (e, id) => {
    if (e?.nativeEvent?.shiftKey) {
      const prevIndex = staff?.findIndex(
        (x) => x._id === selectedRowsId[selectedRowsId?.length - 1],
      );
      const currIndex = staff?.findIndex((x) => x._id === id);
      const startIndex = Math.min(prevIndex, currIndex);
      const endIndex = Math.max(prevIndex, currIndex);
      const shiftSelected = staff?.slice(startIndex, endIndex + 1);

      if (selectedRowsId?.includes(id)) {
        setSelectedRowsId((prev) => [
          ...new Set(prev.filter((x) => !shiftSelected.find((item) => item._id === x))),
        ]);
      } else {
        setSelectedRowsId((prev) => [
          ...new Set([...prev, ...shiftSelected.map((item) => item._id)]),
        ]);
      }
    } else if (id === "all") {
      if (selectedRowsId?.length > 0) {
        setSelectedRowsId([]);
      } else {
        setSelectedRowsId(staff?.map((x) => x?._id));
      }
    } else {
      const existId = selectedRowsId?.includes(id);
      if (!existId) {
        setSelectedRowsId((prev) => [...prev, id]);
      } else {
        setSelectedRowsId((prev) => prev.filter((x) => x !== id));
      }
    }
  };

  // Dynamic sorting applied to local records
  const sortedStaff = useMemo(() => {
    if (!staff || !Array.isArray(staff)) return [];
    return [...staff].sort((a, b) => {
      let aVal = a?.[sortBy];
      let bVal = b?.[sortBy];
      if (sortBy === "name") {
        aVal = `${a?.first_name || ""} ${a?.last_name || ""}`.trim() || a?.name;
        bVal = `${b?.first_name || ""} ${b?.last_name || ""}`.trim() || b?.name;
      } else if (sortBy === "location") {
        aVal = a?.pos_location_info?.length || 0;
        bVal = b?.pos_location_info?.length || 0;
      }
      if (aVal === undefined || aVal === null) aVal = "";
      if (bVal === undefined || bVal === null) bVal = "";
      if (typeof aVal === "string") {
        const cmp = aVal.localeCompare(String(bVal));
        return orderBy.orderByValue === "ASC" ? cmp : -cmp;
      }
      if (aVal < bVal) return orderBy.orderByValue === "ASC" ? -1 : 1;
      if (aVal > bVal) return orderBy.orderByValue === "ASC" ? 1 : -1;
      return 0;
    });
  }, [staff, sortBy, orderBy]);

  const totalRecords = sortedStaff?.length || 0;
  const limitValue = limit === "all" ? totalRecords || 10 : Number(limit || 10);
  const paginatedStaff = limit === "all"
    ? sortedStaff
    : sortedStaff?.slice((Page - 1) * limitValue, Page * limitValue);

  const columns = [
    {
      key: "checkbox",
      width: "40px",
      minWidth: "40px",
      maxWidth: "40px",
      responsiveWidth: "40px",
      headerClassName: "!px-0 w-[40px] min-w-[40px] max-w-[40px] text-center align-middle",
      cellClassName: "!px-0 w-[40px] min-w-[40px] max-w-[40px] text-center align-middle",
      align: "text-center",
      ignoreRowClick: true,
      title: (
        <Checkbox
          checked={
            staff?.length > 0 &&
            selectedRowsId?.length === staff?.length
          }
          indeterminate={
            selectedRowsId?.length > 0 &&
            selectedRowsId?.length < staff?.length
          }
          onChange={(e) => handleSelectRow(e, "all")}
        />
      ),
      render: (row) => (
        <Checkbox
          checked={selectedRowsId?.length > 0 && selectedRowsId?.includes(row._id)}
          onChange={(e) => handleSelectRow(e, row._id)}
        />
      ),
    },
    {
      key: "name",
      title: t("lang_name") || "Name",
      width: "200px",
      minWidth: "150px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => {
        const fullName = `${row?.first_name || ""} ${row?.last_name || ""}`.trim();
        return (
          <span className="font-medium text-xs text-[var(--primary-text-color)]">
            {fullName || row?.name || "-"}
          </span>
        );
      },
    },
    {
      key: "email",
      title: t("lang_email") || "Email",
      width: "220px",
      minWidth: "160px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <span className="text-xs text-[var(--table-text-color)]">
          {row?.email ?? "-"}
        </span>
      ),
    },
    {
      key: "phone",
      title: t("lang_phone") || "Phone",
      width: "160px",
      minWidth: "120px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <span className="text-xs text-[var(--table-text-color)]">
          {row?.phone ?? "-"}
        </span>
      ),
    },
    {
      key: "pin",
      title: t("lang_PIN") || "PIN",
      width: "120px",
      minWidth: "90px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <span className="text-xs text-[var(--table-text-color)]">
          {row?.pin ?? "-"}
        </span>
      ),
    },
    {
      key: "location",
      title: t("lang_Location") || "Location",
      width: "200px",
      minWidth: "140px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => {
        const locationNames = row?.pos_location_info
          ?.map((loc) => loc?.name || loc)
          ?.filter(Boolean)
          ?.join(", ");
        return (
          <span className="text-xs text-[var(--table-text-color)]">
            {locationNames || (row?.pos_location_info?.length ? `${row.pos_location_info.length} Locations` : "-")}
          </span>
        );
      },
    },
    {
      key: "createdAt",
      title: t("lang_created_date") || "Created Date",
      width: "160px",
      minWidth: "130px",
      headerClassName: "px-3 whitespace-nowrap",
      cellClassName: "px-3 whitespace-nowrap",
      render: (row) => (
        <span className="table-text text-xs whitespace-nowrap">
          {row?.createdAt ? formatDate(row.createdAt) : "-"}
        </span>
      ),
    },
    ...dynamicColumns,
    {
      key: "action",
      title: t("lang_action") || "Action",
      width: "100px",
      minWidth: "80px",
      maxWidth: "120px",
      headerClassName: "px-3 text-right",
      cellClassName: "px-3 text-right",
      align: "text-right",
      ignoreRowClick: true,
      render: (row) => (
        <div className="space-x-2 w-full flex items-center justify-end">
          <Tooltip title="Edit" arrow>
            <ButtonComponent
              variant="ghost"
              size="sm"
              onClick={() => Handleedit(row?._id)}
              className="!p-1 text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
            >
              <Edit className="w-[17px] h-[17px]" />
            </ButtonComponent>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <ButtonComponent
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                setDeleteId(row?._id);
                setConfirmationPopup(true);
              }}
              className="!p-1 text-red-500 hover:text-red-600"
            >
              <Trash className="w-[17px] h-[17px]" />
            </ButtonComponent>
          </Tooltip>
        </div>
      ),
    },
  ];

  const tableHeaderFilter = (manageFieldsBtn) => {
    return (
      <div className="relative w-full border-b main-border-color secondary-bg-color px-3 py-2 flex flex-col pe-3 min-h-[50px]">
        <div className="flex flex-wrap items-center justify-between w-full gap-2 min-h-[38px]">
          <div className="flex items-center gap-2 ml-auto hidden md:flex">
            <div className="w-[180px] md:w-[200px]">
              <SearchComponent
                placeholder={t("lang_Search_Staff") || "Search Staff"}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearch("");
                  setPage(1);
                }}
                isClearable={true}
                size="sm"
                iconPlacement="left"
              />
            </div>
            <div className="w-[80px] md:w-[100px] shrink-0 select-sm-height select-hide-label select-no-padding-y">
              <PageLimitDropDownComponent
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                totalRecords={totalRecords}
              />
            </div>
            {manageFieldsBtn}
          </div>

          <Tooltip title={t("lang_filter") || "Filter"} arrow>
            <ButtonComponent
              variant="outline"
              size="sm"
              onClick={(e) => {
                setFilter(true);
                setAnchorEl(e.currentTarget);
              }}
              className="block md:hidden relative !p-1.5 w-8 h-8 shrink-0 ml-auto"
            >
              <Filter className="h-4 w-4 text-[var(--table-text-color)]" />
            </ButtonComponent>
          </Tooltip>
        </div>

        {selectedRowsId?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t main-border-color w-full">
            <span className="text-xs px-2 whitespace-nowrap font-medium">
              {selectedRowsId?.length} {t("lang_selected") || "Selected"}
            </span>
            <span className="text-gray-300">|</span>
            <ButtonComponent
              variant="outline"
              size="sm"
              className="mx-1"
              onClick={() => setOpenBulkEditModal(true)}
              icon={<Edit className="w-3.5 h-3.5" />}
            >
              {t("lang_bulk_edit", "Bulk Edit")}
            </ButtonComponent>
            <ButtonComponent
              variant="danger_outline"
              size="sm"
              icon={<Trash2 className="w-3.5 h-3.5" />}
              onClick={(e) => {
                e.preventDefault();
                setConfirmationPopup(true);
              }}
            >
              {t("lang_delete_selection") || "Delete Selection"}
            </ButtonComponent>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <ConfirmationPopup
        heading="Delete Staff"
        subheading="Do you really want to delete this staff from your store?"
        confirmationPopup={confirmationPopup}
        handleCloseConfirm={handleCloseConfirm}
        runFunction={HandleDelete}
      />
      <div className="flex items-center justify-between w-full overflow-auto pb-4">
        <div className="flex items-center space-x-2">
          <ButtonComponent
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              parentNavigate("/dashboard");
            }}
            className="back-arrow !p-1.5"
          >
            <ArrowLeft />
          </ButtonComponent>
          <div>
            <Breadcrumbs
              crumbs={[
                { name: t("lang_dashboard"), path: `/dashboard` },
                { name: t("lang_staff"), path: "/pos/staff" },
              ]}
            />
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1">
              <h4 className="text-xl md:text-2xl font-semibold text-primary">{t("lang_staff")}</h4>
            </div>
          </div>
        </div>
        <div className="flex items-center">
          <ButtonComponent
            variant="primary"
            icon={<PlusCircle className="w-4 h-4 me-2" />}
            onClick={() => parentNavigate("/pos/staff/add")}
          >
            {t("lang_Add_Staff")}
          </ButtonComponent>
        </div>
      </div>

      <div className="flex justify-between space-x-3 w-full relative">
        <div className="w-full space-y-3">
          <div className="flex w-full h-full overflow-hidden relative min-w-0">
            <div className="w-full">
              <div className="w-full transition-all duration-100 mb-[10px]">
                <CommonTable
                  tableId="posstaff"
                  columns={columns}
                  data={paginatedStaff}
                  loading={dataLoading}
                  skeletonRows={10}
                  emptyMessage={<NoRecords />}
                  tableHeader={tableHeaderFilter}
                  onSort={SortData}
                  sortBy={sortBy}
                  orderBy={orderBy}
                  tableFooter={
                    staff && totalRecords > 0 ? (
                      <PaginationComponent
                        count={Math.ceil(totalRecords / limitValue) || 1}
                        page={Page}
                        onChange={(e, v) => setPage(v)}
                        totalRecords={totalRecords}
                        startRecord={staff?.length ? (Page - 1) * limitValue + 1 : 0}
                        endRecord={staff?.length ? Math.min(Page * limitValue, totalRecords) : 0}
                        limit={limit}
                        recordsLabel="Records"
                      />
                    ) : null
                  }
                  rowClick={(row) => Handleedit(row?._id)}
                  rowClassName={(row) =>
                    `select-none ${selectedRowsId?.find((x) => x === row?._id) ? "selected-row" : ""}`
                  }
                  containerClassName="pos-staff-table sticky-table module-table overflow-x-auto border border-[var(--border-color)] rounded-xl shadow-[var(--shadow-card)]"
                  NEWAPI={handleTableApi}
                  tableDefaults={defaultCustomFields?.defaultPosStaffField}
                  tableWiseAllowedSortKeys={defaultCustomFields?.DEFAULT_POS_STAFF_SORT_KEYS}
                  userState={userContext}
                />
              </div>
            </div>
            <Popover
              open={filter}
              anchorEl={anchorEl}
              onClose={() => {
                setFilter(false);
                setAnchorEl(null);
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              PaperProps={{
                style: { marginTop: "8px", minWidth: "300px", borderRadius: "8px" },
              }}
              className="rounded-[6px]"
            >
              <div className="p-[20px] filter secondary-bg-color">
                <div className="flex justify-between items-center">
                  <p className="flex font-semibold text-sm m-0">
                    <Filter className="me-1 w-4 h-4" />
                    {t("lang_filter")}
                  </p>
                  <ButtonComponent
                    variant="secondary"
                    size="sm"
                    onClick={(e) => resetFilter(e)}
                  >
                    {t("lang_reset_filter")}
                  </ButtonComponent>
                </div>
                <hr className="main-border-color my-3" />
                <div className="grid gap-y-5">
                  <div>
                    <span className="text-sm table-text font-medium mb-1 inline-block capitalize">
                      {t("lang_search")}
                    </span>
                    <SearchComponent
                      placeholder="Search staff"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      onClear={() => {
                        setSearch("");
                        setPage(1);
                      }}
                      iconPlacement="left"
                      isClearable={true}
                    />
                  </div>
                  <div>
                    <span className="text-sm table-text font-medium mb-1 inline-block capitalize">
                      {t("lang_set_items_limit")}
                    </span>
                    <PageLimitDropDownComponent
                      limit={limit}
                      setLimit={setLimit}
                      setPage={setPage}
                      totalRecords={totalRecords}
                    />
                  </div>
                </div>
              </div>
            </Popover>
          </div>
        </div>
      </div>

      {openBulkEditModal && (
        <Dialog
          open={openBulkEditModal}
          disableEscapeKeyDown={true}
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            setOpenBulkEditModal(false);
          }}
          fullScreen
          sx={{
            zIndex: 999999,
            "& .MuiDialog-paper": {
              backgroundColor: "var(--primary-bg-color)",
              color: "var(--primary-text-color)",
            },
          }}
        >
          <BulkEditPosStaff
            bulkStaffIds={selectedRowsId}
            onClose={() => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
            }}
            onSuccess={(updatedCount) => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
              setIsNotification(true);
              const count = typeof updatedCount === "number" ? updatedCount : 1;
              setNotificationMsg(
                `${count} Staff member${count > 1 ? "s" : ""} updated successfully!`
              );
              setSeverity("success");
              getStaff();
            }}
          />
        </Dialog>
      )}
    </div>
  );
};

export default PosStaff;
